const STORAGE_KEY = 'tracking_params';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_VALUE_LENGTH = 500;

const ACCEPTED_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'ref', 'fbclid'];

interface StoredTrackingParams {
  capturedAt: number;
  params: Record<string, string>;
}

export interface TrackingParam {
  key: string;
  value: string;
}

/**
 * Reads accepted tracking params from the current URL and stores them
 * in localStorage. First-touch: does not overwrite existing stored params
 * (unless they have expired past the 30-day TTL).
 */
export const captureTrackingParams = (): void => {
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing) {
      const parsed: StoredTrackingParams = JSON.parse(existing);
      if (typeof parsed?.capturedAt === 'number' && Date.now() - parsed.capturedAt < TTL_MS) {
        return; // first-touch: keep existing, not expired
      }
    }

    const searchParams = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};

    for (const key of ACCEPTED_KEYS) {
      // Check both lowercase and uppercase variants for case-insensitive matching
      const value = searchParams.get(key) || searchParams.get(key.toUpperCase());
      if (value && value.trim()) {
        params[key] = value.trim().slice(0, MAX_VALUE_LENGTH);
      }
    }

    if (Object.keys(params).length > 0) {
      const stored: StoredTrackingParams = {
        capturedAt: Date.now(),
        params
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    // Clear corrupted data so future captures aren't permanently blocked
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Silently fail
    }
  }
};

/**
 * Returns stored tracking params as the [{key, value}] array format
 * the signup mutation expects, or null if nothing is stored / expired.
 */
export const getTrackingParams = (): TrackingParam[] | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const stored: StoredTrackingParams = JSON.parse(raw);
    if (typeof stored?.capturedAt !== 'number' || Date.now() - stored.capturedAt >= TTL_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    const result = Object.entries(stored.params)
      .filter(([, value]) => typeof value === 'string' && value)
      .map(([key, value]) => ({
        key: String(key),
        value: String(value)
      }));

    return result.length > 0 ? result : null;
  } catch {
    return null;
  }
};

/**
 * Removes stored tracking params from localStorage.
 * Call after successful signup.
 */
export const clearTrackingParams = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Silently fail
  }
};
